/**
 * drivers.js — Adapted for RaceProX
 *
 * Drivers are loaded from the DB (org_drivers) via the bootstrap layer.
 * Manual add/edit/remove has been removed (driversPanel.js is gone).
 * The driver list is set once from the bootstrap data and used read-only.
 *
 * @typedef {{
 *   id: string;
 *   firstName: string;
 *   lastName: string;
 *   profileImage: string;
 *   numberImage: string;
 * }} Driver
 */

const TRANSPARENT_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

/** @type {Driver[]} */
let drivers = [];

/**
 * @param {Driver} d
 * @returns {Driver}
 */
function cloneDriver(d) {
  return { ...d };
}

/**
 * @param {string} value
 * @returns {boolean}
 */
export function isValidImageSrc(value) {
  if (typeof value !== 'string' || !value.trim()) return false;
  return value.startsWith('data:') || value.startsWith('/') || value.startsWith('http');
}

/**
 * @param {Driver} driver
 * @returns {boolean}
 */
export function isDriverComplete(driver) {
  return (
    Boolean(driver.firstName?.trim()) &&
    Boolean(driver.lastName?.trim()) &&
    isValidImageSrc(driver.profileImage) &&
    driver.profileImage !== TRANSPARENT_PIXEL
  );
}

/**
 * @param {File} file
 * @returns {Promise<string>}
 */
export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string' && result.startsWith('data:')) {
        resolve(result);
      } else {
        reject(new Error('Could not read image file'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read image file'));
    reader.readAsDataURL(file);
  });
}

/**
 * @returns {Driver[]}
 */
export function getDrivers() {
  return drivers.map(cloneDriver);
}

/**
 * @param {string | null | undefined} id
 * @returns {Driver | undefined}
 */
export function getDriver(id) {
  if (!id) return undefined;
  const found = drivers.find((d) => d.id === id);
  return found ? cloneDriver(found) : undefined;
}

/**
 * @returns {Driver[]}
 */
export function listDrivers() {
  return drivers.filter(isDriverComplete).map(cloneDriver);
}

/**
 * Set drivers from bootstrap data (DB org_drivers).
 * @param {Partial<Driver>[]} arr
 */
export function setDriversFromPreset(arr) {
  if (!Array.isArray(arr) || arr.length === 0) {
    drivers = [];
    return;
  }

  const parsed = [];
  for (const item of arr) {
    if (!item || typeof item !== 'object') continue;
    const id = typeof item.id === 'string' ? item.id.trim() : '';
    const firstName = typeof item.firstName === 'string' ? item.firstName.trim() : '';
    const lastName = typeof item.lastName === 'string' ? item.lastName.trim() : '';
    const profileImage =
      typeof item.profileImage === 'string' && isValidImageSrc(item.profileImage)
        ? item.profileImage
        : TRANSPARENT_PIXEL;
    const numberImage =
      typeof item.numberImage === 'string' && isValidImageSrc(item.numberImage)
        ? item.numberImage
        : TRANSPARENT_PIXEL;
    if (!id) continue;
    parsed.push({ id, firstName, lastName, profileImage, numberImage });
  }

  drivers = parsed;
}

export function resetDriversToDefaults() {
  drivers = [];
}

/**
 * @returns {Driver[]}
 */
export function exportDriversForPreset() {
  return drivers.map(cloneDriver);
}
